import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import {
    User,
    PriceType,
    BondQuantityType,
    PositionAccount,
    Account,
    Rtbs,
    AccountCombinedBalance,
    AccountHistory,
} from '@orbis-systems/orbis-api-middleware';
import { queryOptions } from '@tanstack/react-query';
import { orbis } from '../api';
import { TradingLimitInCurrency } from './types';
import { Portfolio } from '@/types';
import { buildAccountPortfolioWithBreakdownFromCache } from './accounts-portfolio-query-utils';

export type UserInformationResponse = SuccessClientResponse<User>;
export type AccountPortfolioResponse = SuccessClientResponse<Portfolio>;
export type PositionSearchResponse = SuccessClientResponse<PositionAccount[]>;
export type AccountResponse = SuccessClientResponse<Account>;
export type TradingLimitInCurrencyResponse = SuccessClientResponse<TradingLimitInCurrency>;

interface AccountPortfolioFullParams {
    /** (true or false) Allows for loading of quotes for each portfolio entry. Defaults to false. */
    loadQuotes?: boolean;
    loadCost?: boolean;
    /** Load portfolio by account id */
    accountId?: number;
    /** Load portfolio by account */
    account?: string;
    /** @example "false" */
    loadIndustries?: boolean;
    /** @example "false" */
    loadUpgrades?: boolean;
    /** @example "false" */
    loadEarnings?: boolean;
    /**
     * Analyze market caps and get `marketCapWeights`. To be used in conjuction with `loadQuotes`
     * @example "false"
     */
    marketCapBreakdown?: boolean;
    /**
     * Return bond pricing type in `Percentage|Decimal` (optional)
     * @example "Decimal"
     */
    bondPriceType?: PriceType;
    /**
     * Return bond quantity in `Par|Unit` (optional)
     * @example "Par"
     */
    bondQuantityType?: BondQuantityType;
    /** @example "USD" */
    currency?: string;
    /**
     * Which page to return (first page is number 1)
     * @example "1"
     */
    pageNum?: number;
    /**
     * Number of items per page
     * @example "100"
     */
    pageSize?: number;
}

interface PositionSearchParams {
    /** Comma separated list of tickers */
    symbols?: string;
    /**
     * [optional] whether to load quotes for the results
     * @example "false"
     */
    loadQuotes?: boolean;
}

export interface PreferencesChangeParams {
    action: 'set' | 'delete';
    data: Record<string, string>;
}

export type UserAccountInformationParams = Parameters<typeof orbis.user.accountInformation>[0];
export type TradingLimitInCurrencyParams = Parameters<typeof orbis.user.tradingLimitInCurrency>[0];
export type AccountHistoryParams = Parameters<typeof orbis.user.accountHistory>[0];
export type CombinedBalancesV3Params = Parameters<typeof orbis.user.combinedBalancesV3>[0] & {
    accountId: string;
};

interface AccountBalanceParams {
    accountId: string;
    currency?: string;
}

export interface AccountPortfolioWithBreakdownFromBranchQueryParams {
    accountId: number;
    currency: string;
    lastUpdatedAt: number;
}

export type AccountBalanceResponse = SuccessClientResponse<Rtbs>;
export type CombinedBalancesV3Response = SuccessClientResponse<AccountCombinedBalance>;
export type AccountHistoryResponse = SuccessClientResponse<AccountHistory[]>;
export type PreferencesViewResponse = SuccessClientResponse<Record<string, string>>;
export type PreferencesChangeResponse = SuccessClientResponse<Record<string, string>>;

export const userQueries = {
    all: () =>
        queryOptions({
            queryKey: ['user'],
        }),
    userInformation: () =>
        queryOptions<UserInformationResponse>({
            queryKey: [...userQueries.all().queryKey, 'userInformation'],
            queryFn: () => orbis.user.userInformation(),
        }),
    accountInformation: (params: UserAccountInformationParams) =>
        queryOptions<AccountResponse>({
            queryKey: [...userQueries.all().queryKey, 'accountInformation', params],
            queryFn: () => orbis.user.accountInformation(params),
        }),
    accountPortfolioFull: (params: AccountPortfolioFullParams) =>
        queryOptions<AccountPortfolioResponse>({
            queryKey: [...userQueries.all().queryKey, 'portfolio', params],
            queryFn: () => orbis.user.accountPortfolioFull(params),
        }),
    positionSearch: (params: PositionSearchParams) =>
        queryOptions<PositionSearchResponse>({
            queryKey: [...userQueries.all().queryKey, 'positionSearch', params],
            queryFn: () => orbis.user.positionSearch(params),
        }),
    accountBalance: (params: AccountBalanceParams) =>
        queryOptions<AccountBalanceResponse>({
            queryKey: [...userQueries.all().queryKey, 'accountBalance', params],
            queryFn: () => orbis.user.accountBalance(params),
        }),
    tradingLimitInCurrency: (params: TradingLimitInCurrencyParams) =>
        queryOptions<TradingLimitInCurrencyResponse>({
            queryKey: [...userQueries.all().queryKey, 'tradingLimitInCurrency', params],
            queryFn: () => orbis.user.tradingLimitInCurrency(params),
        }),
    combinedBalancesV3: (params: CombinedBalancesV3Params) =>
        queryOptions<CombinedBalancesV3Response>({
            queryKey: [...userQueries.all().queryKey, 'combinedBalancesV3', params],
            queryFn: () => orbis.user.combinedBalancesV3(params),
        }),
    accountHistory: (params: AccountHistoryParams) =>
        queryOptions<AccountHistoryResponse>({
            queryKey: [...userQueries.all().queryKey, 'accountHistory', params],
            queryFn: () => orbis.user.accountHistory(params),
        }),
    preferencesView: () =>
        queryOptions<PreferencesViewResponse>({
            queryKey: [...userQueries.all().queryKey, 'preferencesView'],
            queryFn: () => orbis.user.preferencesView(),
        }),
    accountPortfolioWithBreakdownFromBranch: (params: AccountPortfolioWithBreakdownFromBranchQueryParams) => {
        const { currency, lastUpdatedAt, accountId } = params;
        return queryOptions({
            queryKey: [
                ...userQueries.all().queryKey,
                'accountPortfolioWithBreakdownFromBranch',
                accountId,
                { currency, lastUpdatedAt },
            ],
            queryFn: buildAccountPortfolioWithBreakdownFromCache({ currency, accountId }),
        });
    },
} as const;

export const userMutations = {
    preferencesChange: (params: PreferencesChangeParams): Promise<PreferencesChangeResponse> =>
        orbis.user.preferencesChange(params.action, params.data),
};
